import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Lock, Mail, ArrowRight, Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(form.email, form.password);
    setLoading(false);
    if (res.ok) {
      toast.success("Bem-vindo!");
      const params = new URLSearchParams(location.search);
      const nextParam = params.get("next");
      const dest = nextParam || location.state?.from?.pathname || (res.user.role === "client" ? "/portal" : "/admin");
      nav(dest, { replace: true });
    } else {
      toast.error(res.error);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Visual side */}
      <div className="hidden lg:flex lg:w-1/2 relative theme-marketing overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1505624198937-c704aff72608?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxwcmVtaXVtJTIwY293b3JraW5nJTIwc3BhY2V8ZW58MHx8fHwxNzc4NTc1Mzk2fDA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#0A1128] via-[#0A1128]/95 to-[#14213D]/90" />
        <div className="relative z-10 p-12 flex flex-col justify-between h-full">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-md border border-[#D4AF37]/40 flex items-center justify-center">
              <span className="font-display font-bold text-[#D4AF37] text-lg">VM</span>
            </div>
            <div className="leading-tight">
              <div className="font-display font-semibold text-white text-base">VM Corporate</div>
              <div className="text-[10px] text-white/50 uppercase tracking-[0.2em]">Coworking Premium</div>
            </div>
          </Link>
          <div>
            <h2 className="font-display text-4xl text-white font-light leading-tight">
              Bem-vindo de volta.
              <span className="block italic text-[#D4AF37] mt-2">Sua produtividade espera.</span>
            </h2>
            <p className="text-white/60 mt-4 max-w-md">Acesse o portal para gerenciar reservas, faturas e contratos com poucos cliques.</p>
          </div>
        </div>
      </div>

      {/* Form side */}
      <div className="flex-1 bg-white flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <Link to="/" className="lg:hidden flex items-center gap-3 mb-10 justify-center">
            <div className="w-10 h-10 rounded-md bg-[#14213D] flex items-center justify-center">
              <span className="font-display font-bold text-[#D4AF37] text-sm">VM</span>
            </div>
            <div className="font-display font-semibold text-[#14213D]">VM Corporate</div>
          </Link>

          <div className="text-xs uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-3">Entrar</div>
          <h1 className="font-display text-3xl text-[#14213D] font-light mb-1">Acesse sua conta</h1>
          <p className="text-sm text-gray-500 mb-8">Entre com seu e-mail e senha cadastrados.</p>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.12em] text-gray-500 font-semibold">E-mail</label>
              <div className="relative mt-2">
                <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  required type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full bg-gray-50 border border-gray-200 rounded-md pl-10 pr-4 py-3 text-[#14213D] focus:border-[#14213D] outline-none"
                  data-testid="login-email"
                  placeholder="seu@email.com"
                />
              </div>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.12em] text-gray-500 font-semibold">Senha</label>
              <div className="relative mt-2">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  required type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className="w-full bg-gray-50 border border-gray-200 rounded-md pl-10 pr-12 py-3 text-[#14213D] focus:border-[#14213D] outline-none"
                  data-testid="login-password"
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(s => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded text-gray-400 hover:text-[#14213D] hover:bg-gray-100"
                  aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                  aria-pressed={showPassword}
                  tabIndex={-1}
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <button type="submit" disabled={loading} className="w-full bg-[#14213D] text-white font-semibold py-3 rounded-md hover:bg-[#1F335E] transition disabled:opacity-50 flex items-center justify-center gap-2" data-testid="login-submit">
              {loading ? "Entrando…" : <>Entrar <ArrowRight size={16} /></>}
            </button>
          </form>

          <div className="mt-4 text-center">
            <Link to="/esqueci-senha" className="text-xs text-gray-500 hover:text-[#14213D] hover:underline" data-testid="link-forgot-password">
              Esqueci minha senha
            </Link>
          </div>

          <div className="mt-6 text-center text-sm text-gray-500">
            Ainda não tem conta?{" "}
            <Link to="/register" className="text-[#14213D] font-semibold hover:text-[#D4AF37]" data-testid="link-register">Cadastre-se</Link>
          </div>
          <div className="mt-3 text-center text-xs text-gray-400">
            <Link to="/" className="hover:text-[#14213D]">← Voltar ao site</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
